import { makeObservable, action, observable } from 'mobx';

import {
  GetAreasByCoordinatesDocument,
  GetInfoByAreaDocument,
  GetLayersByCoordinatesDocument,
  GetLayersByUrlDocument,
  GetNearbyAreasDocument,
  GetPropertiesByCoordinatesDocument,
  GetSubAreasByAreaDocument,
  GetTrendsDocument,
  Layer,
} from '@graphql';

import type {
  GetAreasByCoordinatesAreas,
  GetInfoByAreaOfferingsAmount,
  GetInfoByAreaSubAreas,
  GetInfoByAreaTopCities,
  GetInfoByAreaQueryVariables,
  GetLayersByUrlQuery,
  GetNearbyAreasNearbyAreas,
  GetNearbyAreasQuery,
  GetNearbyAreasQueryVariables,
  GetPropertiesByCoordinatesQueryVariables,
  GetTrendsQuery,
  GetTrendsTrends,
  GetTrendsQueryVariables,
  GetPropertiesByCoordinatesQuery,
  GetInfoByAreaQuery,
  GetAreasByCoordinatesQuery,
  GetAreasByCoordinatesQueryVariables,
  GetLayersByCoordinatesQuery,
  GetLayersByCoordinatesQueryVariables,
  GetLayersByUrlQueryVariables,
  GetSubAreasByAreaQuery,
  GetSubAreasByAreaQueryVariables,
  GetInfoByAreaQueryCombined,
} from '@graphql';
import type { PriceBoxData } from '@lib/types';
import type { ApolloClient } from '@lib/apolloClient';

let layerStore: Layers | null = null;

export type PriceTableType = keyof PricesTable;

type MappedPriceTableData<T> = {
  whiteList: T[];
  blackList: T[];
};

type PriceTableLayer<T> = {
  perPage: number;
  count: number;
  page: number;
  originalData: T[];
  paginatedData: T[];
};

type PricesTable = {
  topCitiesWL: PriceTableLayer<GetInfoByAreaTopCities>;
  topCitiesBL: PriceTableLayer<GetInfoByAreaTopCities>;
  priceListWL: PriceTableLayer<GetInfoByAreaSubAreas>;
  priceListBL: PriceTableLayer<GetInfoByAreaSubAreas>;
  nearbyAreasWL: PriceTableLayer<GetNearbyAreasNearbyAreas>;
  nearbyAreasBL: PriceTableLayer<GetNearbyAreasNearbyAreas>;
  nearbyDistrictsWL: PriceTableLayer<GetNearbyAreasNearbyAreas>;
  nearbyDistrictsBL: PriceTableLayer<GetNearbyAreasNearbyAreas>;
};

class Layers {
  areaData: GetAreasByCoordinatesAreas | null = null;
  areaClicked = false;
  positionSelected = false;
  showWidgetX = false;
  trendsData: GetTrendsTrends[] = [];
  priceBoxData: PriceBoxData = {
    areaTitle: '',
    avgPriceHouseBuy: 0,
    minPriceHouseBuy: 0,
    maxPriceHouseBuy: 0,
    avgPriceApartmentBuy: 0,
    minPriceApartmentBuy: 0,
    maxPriceApartmentBuy: 0,
  };
  nearbyData: GetNearbyAreasQuery | Record<never, never> = {};
  pricesTable: PricesTable = {
    topCitiesWL: {
      perPage: 25,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    topCitiesBL: {
      perPage: 25,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    priceListWL: {
      perPage: 7,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    priceListBL: {
      perPage: 7,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    nearbyAreasWL: {
      perPage: 4,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    nearbyAreasBL: {
      perPage: 4,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    nearbyDistrictsWL: {
      perPage: 7,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
    nearbyDistrictsBL: {
      perPage: 7,
      count: 0,
      page: 0,
      originalData: [],
      paginatedData: [],
    },
  };
  offeringsAmount: GetInfoByAreaOfferingsAmount = {
    housesToBuy: 0,
    apartmentsToBuy: 0,
  };

  setVisibilityWidgetX(value: boolean): void {
    if (value) {
      document.body.classList.add('show-widget');
    } else {
      document.body.classList.remove('show-widget');
    }

    this.showWidgetX = value;
  }

  setPositionSelected(value: boolean): void {
    this.positionSelected = value;
  }

  setPriceBoxData(
    prices: Partial<
      Omit<GetAreasByCoordinatesAreas, '__typename'> & Pick<PriceBoxData, 'areaTitle'>
    > | null,
    title?: string,
  ): void {
    if (!prices) {
      return;
    }

    (Object.keys(this.priceBoxData) as (keyof PriceBoxData)[]).forEach((item) => {
      const pricesValue = prices[item];
      const currentItemValue = this.priceBoxData[item];

      if (pricesValue !== currentItemValue) {
        this.priceBoxData[item] = pricesValue as never;
      }
    });

    if (title && this.priceBoxData && title !== this.priceBoxData.areaTitle) {
      this.priceBoxData.areaTitle = title;
    } else if (this.priceBoxData.areaTitle !== prices.districtSmallName) {
      this.priceBoxData.areaTitle = prices.districtSmallName || '';
    }
  }

  priceTableNextPage<T extends PriceTableType>(tableType: T): void {
    if (!this.isNextPageExist(tableType)) {
      return;
    }

    this.pricesTable[tableType].page += 1;
    const start = (this.pricesTable[tableType].page - 1) * this.pricesTable[tableType].perPage;
    const data = this.pricesTable[tableType].originalData.slice(
      start,
      this.pricesTable[tableType].perPage + start,
    );

    if (this.pricesTable[tableType].page === 1) {
      this.pricesTable[tableType].paginatedData = data;
    } else {
      this.pricesTable[tableType].paginatedData.push(...(data as never[]));
    }
  }

  resetPage(tableType: PriceTableType): void {
    this.pricesTable[tableType].page = 0;
    this.priceTableNextPage(tableType);
  }

  isNextPageExist = (tableType: PriceTableType): boolean =>
    this.pricesTable[tableType].page + 1 <=
    Math.ceil(this.pricesTable[tableType].count / this.pricesTable[tableType].perPage);

  async getArea(
    apolloClient: ApolloClient,
    lat: number,
    lng: number,
  ): Promise<GetAreasByCoordinatesQuery> {
    const queryVariables = { lat, lng };

    const { data } = await apolloClient.query<
      GetAreasByCoordinatesQuery,
      GetAreasByCoordinatesQueryVariables
    >({
      query: GetAreasByCoordinatesDocument,
      variables: queryVariables,
    });

    if (data?.areasByCoordinates?.areas?.length) {
      this.areaData = data.areasByCoordinates.areas[0];
      if (!this.areaClicked) {
        this.areaClicked = true;
      }

      this.setPriceBoxData(this.areaData);
    }

    return data;
  }

  getAreaData(): GetAreasByCoordinatesAreas | null {
    return this.areaData;
  }

  async getLayersByCoordinates(
    apolloClient: ApolloClient,
    lat: number,
    lng: number,
    layer: Layer,
  ): Promise<GetLayersByCoordinatesQuery> {
    const queryVariables = { lat, lng, layer };

    const { data } = await apolloClient.query<
      GetLayersByCoordinatesQuery,
      GetLayersByCoordinatesQueryVariables
    >({
      query: GetLayersByCoordinatesDocument,
      variables: queryVariables,
    });

    return data;
  }

  async getLayersByUrl(
    apolloClient: ApolloClient,
    url: string,
  ): Promise<GetLayersByUrlQuery['layersByUrl']> {
    const { data } = await apolloClient.query<GetLayersByUrlQuery, GetLayersByUrlQueryVariables>({
      query: GetLayersByUrlDocument,
      variables: { url },
    });

    return data.layersByUrl;
  }

  async getPropertiesByCoordinates(
    apolloClient: ApolloClient,
    bounds: GetPropertiesByCoordinatesQueryVariables,
  ): Promise<GetPropertiesByCoordinatesQuery> {
    const { data } = await apolloClient.query<
      GetPropertiesByCoordinatesQuery,
      GetPropertiesByCoordinatesQueryVariables
    >({
      query: GetPropertiesByCoordinatesDocument,
      variables: bounds,
    });

    return data;
  }

  setData(data?: GetInfoByAreaQueryCombined): void {
    if (!data?.infoByArea) {
      return;
    }

    if (data.offeringsAmount) {
      this.offeringsAmount = data.offeringsAmount;
    }

    if (!this.areaClicked) {
      this.setPriceBoxData(data.infoByArea, data.infoByArea.name);
    }
    const mappedData = this.mapPriceTableData(data.subAreasByArea?.subAreas || []);

    this.pricesTable.priceListWL.originalData = mappedData.whiteList;
    this.pricesTable.priceListWL.count = mappedData.whiteList.length;
    this.resetPage('priceListWL');

    this.pricesTable.priceListBL.originalData = mappedData.blackList;
    this.pricesTable.priceListBL.count = mappedData.blackList.length;
    this.resetPage('priceListBL');

    const topCitiesMappedData = this.mapPriceTableData(data.topCities);

    this.pricesTable.topCitiesWL.originalData = topCitiesMappedData.whiteList;
    this.pricesTable.topCitiesWL.count = topCitiesMappedData.whiteList.length;
    this.resetPage('topCitiesWL');

    this.pricesTable.topCitiesBL.originalData = topCitiesMappedData.blackList;
    this.pricesTable.topCitiesBL.count = topCitiesMappedData.blackList.length;
    this.resetPage('topCitiesBL');
  }

  setTrends(trends: GetTrendsQuery): void {
    if (trends) {
      this.trendsData = trends.trends;
    }
  }

  async getLayer(
    apolloClient: ApolloClient,
    variables: GetInfoByAreaQueryVariables,
  ): Promise<GetInfoByAreaQueryCombined> {
    const [area, subArea] = await Promise.allSettled([
      apolloClient.query<GetInfoByAreaQuery, GetInfoByAreaQueryVariables>({
        query: GetInfoByAreaDocument,
        variables,
      }),
      apolloClient.query<GetSubAreasByAreaQuery, GetSubAreasByAreaQueryVariables>({
        query: GetSubAreasByAreaDocument,
        variables,
      }),
    ]);

    if (area.status === 'rejected') {
      throw new Error('GetInfoByAreaQuery error');
    }

    let data: GetInfoByAreaQueryCombined = area.value.data;

    if (subArea.status === 'fulfilled') {
      data = { ...area.value.data, ...subArea.value.data };
    }

    this.setData(data);
    return data;
  }

  async getTrends(
    apolloClient: ApolloClient,
    variables: GetTrendsQueryVariables,
  ): Promise<GetTrendsQuery> {
    const { data } = await apolloClient.query<GetTrendsQuery, GetTrendsQueryVariables>({
      query: GetTrendsDocument,
      variables,
    });

    this.setTrends(data);
    return data;
  }

  mapPriceTableData<T>(data: T[]): MappedPriceTableData<T> {
    const whiteList: T[] = [];
    const blackList: T[] = [];

    data.forEach((item) => {
      if ((item as { seoUrl?: string }).seoUrl) {
        whiteList.push(item);
      } else {
        blackList.push(item);
      }
    });

    return { whiteList, blackList };
  }

  setNearbyData(data: GetNearbyAreasQuery, layer: Layer): void {
    if (data.nearbyAreasByArea) {
      if (layer === Layer.District || layer === Layer.DistrictSmall) {
        const mappedData = this.mapPriceTableData(data.nearbyAreasByArea.nearbyAreas);
        this.pricesTable.nearbyDistrictsWL.originalData = mappedData.whiteList;
        this.pricesTable.nearbyDistrictsWL.count = mappedData.whiteList.length;
        this.resetPage('nearbyDistrictsWL');

        this.pricesTable.nearbyDistrictsBL.originalData = mappedData.blackList;
        this.pricesTable.nearbyDistrictsBL.count = mappedData.blackList.length;
        this.resetPage('nearbyDistrictsBL');
      } else {
        const mappedData = this.mapPriceTableData(data.nearbyAreasByArea.nearbyAreas);
        this.pricesTable.nearbyAreasWL.originalData = mappedData.whiteList;
        this.pricesTable.nearbyAreasWL.count = mappedData.whiteList.length;
        this.resetPage('nearbyAreasWL');

        this.pricesTable.nearbyAreasBL.originalData = mappedData.blackList;
        this.pricesTable.nearbyAreasBL.count = mappedData.blackList.length;
        this.resetPage('nearbyAreasBL');
      }
    }

    this.nearbyData = data;
  }

  async getNearbyAreas(
    apolloClient: ApolloClient,
    variables: GetNearbyAreasQueryVariables,
  ): Promise<GetNearbyAreasQuery> {
    const { data } = await apolloClient.query<GetNearbyAreasQuery, GetNearbyAreasQueryVariables>({
      query: GetNearbyAreasDocument,
      variables,
    });

    this.setNearbyData(data, variables.layer);
    return data;
  }

  constructor() {
    makeObservable(this, {
      areaData: observable,
      areaClicked: observable,
      positionSelected: observable,
      showWidgetX: observable,
      trendsData: observable,
      priceBoxData: observable,
      nearbyData: observable,
      pricesTable: observable,
      offeringsAmount: observable,

      setPositionSelected: action,
      setVisibilityWidgetX: action,
      setPriceBoxData: action,
      priceTableNextPage: action,
      resetPage: action,
      getArea: action,
      getLayersByCoordinates: action,
      setData: action,
      setTrends: action,
      getPropertiesByCoordinates: action,
      getLayer: action,
      setNearbyData: action,
      getTrends: action,
      getNearbyAreas: action,
    });
  }
}

// eslint-disable-next-line import/no-default-export
export default function getLayerStore(): Layers {
  if (layerStore === null) {
    layerStore = new Layers();
  }
  return layerStore;
}
